/**
 * Reader-app-mode detection + feature gating.
 *
 * When TruBeauté is running inside the iOS wrapper (Capacitor build),
 * we operate under Apple's "Reader app" policy:
 *   • NO mention of purchase, upgrade, or pricing anywhere in-app.
 *   • NO external payment links (would trigger Guideline 3.1.1 / 3.1.3 rejection).
 *   • Pro features unlock for already-paid subscribers who sign in.
 *
 * This module is the single source of truth for that gating logic.
 * Import `useReaderAppMode()` in any component that needs to hide
 * upgrade surfaces.
 *
 * Detection order (highest → lowest priority):
 *   1. Runtime platform: `window.Capacitor.getPlatform() === "ios"` when
 *      running inside the iOS Capacitor shell.
 *   2. Build-time env: `REACT_APP_READER_APP=true` (set in CI for the
 *      iOS build pipeline if we ever want to force the flag).
 *   3. URL query: `?readerapp=1` so the founder can preview the iOS
 *      experience without building the native shell.
 *
 * Android is NOT reader-app — Google Play allows external payment
 * links, so the Play Store build behaves exactly like the web.
 */

const READER_APP_PREVIEW_PARAM = "readerapp";
const READER_APP_STORAGE_KEY = "trubeaute.__reader_app_preview";

/**
 * Synchronous check usable outside React. Safe in SSR.
 */
export const isReaderAppMode = () => {
  if (typeof window === "undefined") return false;

  // 1. Native iOS shell (Capacitor wraps our React build in WKWebView)
  try {
    const cap = window.Capacitor;
    if (cap && typeof cap.getPlatform === "function") {
      if (cap.getPlatform() === "ios") return true;
    }
  } catch {
    /* no-op */
  }

  // 2. Build-time env flag (CRA inlines REACT_APP_* at build)
  if (process.env.REACT_APP_READER_APP === "true") return true;

  // 3. Founder preview: ?readerapp=1 sticks for the rest of the session.
  try {
    if (window.location.search.includes(`${READER_APP_PREVIEW_PARAM}=1`)) {
      window.sessionStorage?.setItem(READER_APP_STORAGE_KEY, "1");
      return true;
    }
    if (window.sessionStorage?.getItem(READER_APP_STORAGE_KEY) === "1") {
      return true;
    }
  } catch {
    /* no-op */
  }

  return false;
};

/**
 * React hook wrapper so the component re-renders if the flag changes
 * (e.g. user navigates in with `?readerapp=1`). The value is fixed
 * for the session in practice.
 */
import { useMemo } from "react";
export const useReaderAppMode = () => useMemo(() => isReaderAppMode(), []);
